import React, { useCallback, useEffect, useState } from 'react';
import { Bookmark, Search } from 'react-bootstrap-icons';

import { Input, Select, Space, Typography } from 'antd';

import { useDebounce } from 'usehooks-ts';

import EmptyState from '@/components/shared/EmptyState';
import HorizontalList from '@/components/shared/HorizontalList';
import PageTitle from '@/components/shared/PageTitle';
import Wrapper from '@/components/shared/Wrapper';
import { useWindowScrollReachedEnd } from '@/hooks/useWindowScrollReachedEnd';
import { IPaginationParams } from '@/interfaces/pagination';
import { useGetAllSavedLessonsQuery } from '@/services/savedLessons';
import styled, { StyledProp } from '@/styled';

import Card from './Card';

const SavedLessons = (props: StyledProp) => {
  const [filters, setFilters] = useState<Partial<IPaginationParams>>({ page: 1, size: 100 });

  const debouncedSearch = useDebounce(filters, 600);
  const reachedEnd = useWindowScrollReachedEnd();

  const { data: response, isFetching, isLoading } = useGetAllSavedLessonsQuery(debouncedSearch);

  const fetchLessons = useCallback(
    (params: Partial<IPaginationParams>) => {
      if (isFetching) return;
      setFilters(filters => ({ ...filters, ...params }));
    },
    [isFetching]
  );

  useEffect(() => {
    if (!response || isFetching || !reachedEnd) return;

    if (response.total_pages > response.page.page) {
      setFilters(filters => ({
        ...filters,
        page: (filters?.page ?? 0) + 1
      }));
    }
  }, [reachedEnd, response, isFetching]);

  const handleSearch = (event: React.ChangeEvent<{ value: string }>) => {
    setFilters(filters => ({ ...filters, search: event.target.value || undefined, page: 1 }));
  };

  const handleOrder = (order: number) => {
    const orderMapper = [
      { order: 'ASC', orderBy: 'created_at' },
      { order: 'DESC', orderBy: 'created_at' },
      { order: 'ASC', orderBy: 'title' },
      { order: 'ASC', orderBy: 'course_title' }
    ];

    fetchLessons({ ...orderMapper[order], page: 1 });
  };

  const orderByMenu = ['Mais recentes', 'Mais antigos', 'Alfabética A-Z', 'Cursos'];
  const hasSavedLessons = response?.data.length > 0;

  return (
    <Wrapper className={props.className}>
      <PageTitle
        className='saved-lesson-titleSpace'
        title='Aulas Salvas'
        endContent={
          <Space size={16}>
            <Input size='large' placeholder='Buscar' prefix={<Search />} onChange={handleSearch} />

            <Select
              onChange={handleOrder}
              placeholder={<Typography.Text strong>Exibir por</Typography.Text>}
              options={orderByMenu.map((label, value) => ({ value, label }))}
              size='large'
            />
          </Space>
        }
      />
      {!hasSavedLessons && !isFetching && (
        <EmptyState
          icon={<Bookmark />}
          title='Suas aulas salvas aparecerão aqui.'
          description='Aqui você poderá acessar com facilidade todas as suas aulas salvas.'
        />
      )}

      <HorizontalList isFetching={isLoading}>
        {response?.data?.map(lesson => (
          <Card {...lesson} key={lesson.id} />
        ))}
      </HorizontalList>
    </Wrapper>
  );
};

export default styled(SavedLessons)`
  .ant-select {
    min-width: 150px;
  }
`;
